import { useRef, useState, useEffect } from 'react';
import { useTTSStore } from '../../store/tts';
import toast from 'react-hot-toast';

function AudioPlayer() {
  const audioRef = useRef(null);
  const { 
    currentAudio, 
    clearCurrentAudio, 
    generatedAudios, 
    loadGeneratedAudio, 
    deleteGeneratedAudio 
  } = useTTSStore();
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioError, setAudioError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);

  useEffect(() => {
    if (currentAudio?.url && audioRef.current) {
      setAudioError(null);
      audioRef.current.load();
    }
  }, [currentAudio?.url]);

  // Apply playback speed when it changes
  useEffect(() => {
    if (audioRef.current && currentAudio?.playbackSpeed) {
      audioRef.current.playbackRate = currentAudio.playbackSpeed;
    }
  }, [currentAudio?.playbackSpeed]);

  if (!currentAudio && generatedAudios.length === 0) {
    return null;
  }

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      // Apply initial playback speed
      if (currentAudio?.playbackSpeed) {
        audioRef.current.playbackRate = currentAudio.playbackSpeed;
      }
    }
  };

  const handleSeek = (e) => {
    const time = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = currentAudio.url;
    link.download = `voxifyai_${currentAudio.id}.wav`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleLoadAudio = async (audio) => {
    setIsLoadingAudio(true);
    setAudioError(null);
    const result = await loadGeneratedAudio(audio);
    setIsLoadingAudio(false);
    
    if (!result.success) {
      toast.error('Failed to load audio');
    } else {
      setShowHistory(false);
    }
  };

  const handleDeleteAudio = async (e, audioId) => {
    e.stopPropagation();
    if (confirm('Delete this audio?')) {
      const result = await deleteGeneratedAudio(audioId);
      if (result.success) {
        toast.success('Audio deleted');
      } else {
        toast.error('Failed to delete');
      }
    }
  };

  return (
    <div className="card fade-in">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold">Generated Audio</h3>
        <div className="flex items-center space-x-2">
          {generatedAudios.length > 0 && (
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="text-sm text-purple-400 hover:text-purple-300"
            >
              {showHistory ? 'Hide' : 'Show'} History ({generatedAudios.length})
            </button>
          )}
          {currentAudio && (
            <button
              onClick={clearCurrentAudio}
              className="text-gray-400 hover:text-white"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Loading State */}
      {isLoadingAudio && (
        <div className="flex flex-col items-center py-8">
          <span className="spinner w-8 h-8 mb-2"></span>
          <p className="text-gray-400 text-sm">Loading audio...</p>
        </div>
      )}

      {/* Current Audio Player */}
      {currentAudio && currentAudio.url && !isLoadingAudio && (
        <>
          <audio
            ref={audioRef}
            src={currentAudio.url}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onError={(e) => setAudioError('Failed to load audio. Please try again.')}
          />

          {audioError && (
            <div className="text-red-400 text-sm text-center mb-4">
              {audioError}
            </div>
          )}

          <div className="space-y-4">
            {/* Audio info */}
            {currentAudio.text && (
              <p className="text-sm text-gray-400 truncate" title={currentAudio.text}>
                "{currentAudio.text.substring(0, 60)}{currentAudio.text.length > 60 ? '...' : ''}"
              </p>
            )}

            {/* Voice and language info */}
            <div className="flex flex-wrap gap-2 text-xs">
              {currentAudio.voiceName && (
                <span className="bg-purple-600/30 text-purple-300 px-2 py-1 rounded">
                  🎤 {currentAudio.voiceName}
                </span>
              )}
              {currentAudio.languageName && (
                <span className="bg-blue-600/30 text-blue-300 px-2 py-1 rounded">
                  🌐 {currentAudio.languageName}
                </span>
              )}
              {currentAudio.speed && currentAudio.speed !== 1.0 && (
                <span className="bg-green-600/30 text-green-300 px-2 py-1 rounded">
                  ⚡ {currentAudio.speed.toFixed(1)}x
                </span>
              )}
            </div>

            {/* Progress bar */}
            <div className="space-y-2">
              <input
                type="range"
                min={0}
                max={duration || 0}
                value={currentTime}
                onChange={handleSeek}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
              />
              <div className="flex justify-between text-sm text-gray-400">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center space-x-4">
              <button
                onClick={togglePlay}
                className="w-14 h-14 rounded-full bg-purple-600 hover:bg-purple-700 flex items-center justify-center transition-colors"
              >
                {isPlaying ? (
                  <span className="text-2xl">⏸</span>
                ) : (
                  <span className="text-2xl ml-1">▶</span>
                )}
              </button>
              
              <button
                onClick={handleDownload}
                className="btn btn-secondary py-2 px-4"
              >
                ⬇ Download
              </button>
            </div>

            {/* Info */}
            <div className="text-center text-sm text-gray-400 space-y-1">
              {currentAudio.processing_time && (
                <p>
                  Generated in {currentAudio.processing_time.toFixed(2)}s
                  {currentAudio.cached && ' (cached)'}
                </p>
              )}
              {currentAudio.playbackSpeed && currentAudio.playbackSpeed !== 1.0 && (
                <p className="text-purple-400">
                  Playback: {currentAudio.playbackSpeed.toFixed(2)}x
                </p>
              )}
            </div>
          </div>
        </>
      )}

      {/* Generated Audios History */}
      {showHistory && generatedAudios.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-700">
          <h4 className="text-sm font-medium mb-3">Previous Generations</h4>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {generatedAudios.map((audio) => (
              <div
                key={audio.id}
                onClick={() => handleLoadAudio(audio)}
                className={`p-3 rounded-lg cursor-pointer transition-all ${
                  currentAudio?.id === audio.id
                    ? 'bg-purple-600/20 border border-purple-500'
                    : 'bg-gray-700/50 hover:bg-gray-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate" title={audio.text}>
                      {audio.text?.substring(0, 40)}{audio.text?.length > 40 ? '...' : ''}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <span className="text-xs bg-purple-600/30 text-purple-300 px-1.5 py-0.5 rounded">
                        {audio.voiceName || audio.voiceId}
                      </span>
                      <span className="text-xs bg-blue-600/30 text-blue-300 px-1.5 py-0.5 rounded">
                        {audio.languageName || audio.language || 'en'}
                      </span>
                      {audio.createdAt && (
                        <span className="text-xs text-gray-500">
                          {new Date(audio.createdAt).toLocaleTimeString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleDeleteAudio(e, audio.id)}
                    className="ml-2 p-1.5 hover:bg-gray-600 rounded text-gray-400 hover:text-red-400"
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No current audio but has history */}
      {!currentAudio && generatedAudios.length > 0 && !showHistory && !isLoadingAudio && (
        <div className="text-center py-4">
          <p className="text-gray-400 text-sm mb-2">No audio playing</p>
          <button
            onClick={() => setShowHistory(true)}
            className="text-purple-400 hover:text-purple-300 text-sm"
          >
            View {generatedAudios.length} previous generation{generatedAudios.length !== 1 ? 's' : ''}
          </button>
        </div>
      )}
    </div>
  );
}

export default AudioPlayer;
